import api from './client';

/**
 * Phase 37A.5 — Wallet Top-up v2 client (real Stripe Checkout).
 *
 * The three functions below wrap the v2 endpoints introduced in Task
 * 37A.2 and Task 37A.3. Every call is a no-op unless the backend's
 * `wallet_topup_v2` feature flag is on (in which case the corresponding
 * endpoint is registered but returns 404). Callers can read the flag
 * value from `getShopConfig()` and route users to either the v2 flow
 * (this module) or the legacy flow (`api/shop.js::createTopup`).
 *
 * Wallet crediting is NEVER performed by the frontend — the webhook
 * (Task 37A.3) is the single source of credit. These helpers only
 * initiate a Stripe Checkout Session and poll for its status.
 */

/**
 * Create a Stripe Checkout Session for a wallet top-up.
 *
 * @param {number} eurAmount — EUR amount from the allowed TOPUP_PACKAGES.
 *   The backend rejects any value not in the allow-list.
 * @param {string} [originUrl] — optional override for the SPA origin
 *   (defaults to `window.location.origin`); useful when the SPA is
 *   served from a domain different than the request origin.
 * @returns {Promise<{
 *   internal_session_id: string,
 *   stripe_session_id: string,
 *   checkout_url: string,
 *   eur_amount: number,
 *   credits_amount: number,
 *   mock: boolean,
 * }>}
 */
export const createTopupCheckoutV2 = async (eurAmount, originUrl = null) => {
  const payload = { eur_amount: Number(eurAmount) };
  if (originUrl) payload.origin_url = originUrl;
  const res = await api.post('/wallet/topup/checkout', payload);
  return res.data;
};

/**
 * Poll the current status of a v2 top-up session.
 *
 * @param {string} internalSessionId — Nymia-side UUID returned by
 *   `createTopupCheckoutV2()`.
 * @returns {Promise<{
 *   internal_session_id: string,
 *   stripe_session_id: string | null,
 *   status: "pending" | "completed" | "cancelled" | "expired",
 *   payment_status: string | null,
 *   credits_amount: number,
 *   eur_amount: number,
 *   created_at: string,
 *   completed_at: string | null,
 * }>}
 */
export const getTopupCheckoutStatusV2 = async (internalSessionId) => {
  const res = await api.get(
    `/wallet/topup/checkout/${encodeURIComponent(internalSessionId)}/status`
  );
  return res.data;
};

/**
 * Convenience helper — polls the status endpoint on an exponential
 * backoff until the session reaches a terminal state or the max
 * number of attempts is exhausted.
 *
 * The webhook (Task 37A.3) typically flips the session to `completed`
 * within 1-3 seconds of the buyer clicking "Pay" on Stripe's hosted
 * page, so a 15-attempt loop with 1.5x backoff covers the p99 tail
 * without hammering the API.
 *
 * @param {string} internalSessionId
 * @param {(status: object) => void} [onTick] — optional callback fired
 *   on every poll cycle. Useful to update UI ("still processing…").
 * @param {object} [opts] — { maxAttempts, initialDelayMs, maxDelayMs }
 * @returns {Promise<object>} — final status document, guaranteed to
 *   be either terminal ({status: "completed"|"cancelled"|"expired"}) or
 *   the last poll response if the max attempts are exhausted.
 */
export const pollTopupUntilTerminal = async (
  internalSessionId,
  onTick = null,
  opts = {}
) => {
  const {
    maxAttempts = 15,
    initialDelayMs = 800,
    maxDelayMs = 6000,
  } = opts;

  const isTerminal = (s) => ['completed', 'cancelled', 'expired'].includes(s?.status);

  let delay = initialDelayMs;
  let last = null;
  for (let i = 0; i < maxAttempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    last = await getTopupCheckoutStatusV2(internalSessionId);
    if (typeof onTick === 'function') onTick(last);
    if (isTerminal(last)) return last;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(Math.round(delay * 1.5), maxDelayMs);
  }
  return last;
};
